import {
  CSSProperties,
  MouseEventHandler,
  useEffect,
  useMemo,
  useState,
} from 'react';
import TagsBoxSingleTagShowAll from './tagsBoxSingleTag';
import { cn } from 'utils/style-utils';
import { ExternalProperty } from 'crono-fe-common/types/externalProperty';
import { Tooltip } from '@mui/material';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@crono-one/design-system';
import debounce from 'lodash/debounce';

export interface TagsBoxShowAllViewProps {
  //The tags to render, already fetched and filtered by tableType
  tags: ExternalProperty[];
  currentSituation?: (string | null)[];
  handleClickTag?: (
    option: string | null,
    externalProperty: ExternalProperty,
    index: number,
  ) => void;
  //If this is set to true, it means that the tags marked as isEditable false should be disabled (because we
  // are in accout/prospect page for example).
  // This is false by default, because for strategies and templates we want to show and edit all the tags, even if they are not editable
  blockIsEditable?: boolean;
  //If true we do not allow edit. Default false
  disableEdit?: boolean;
  //If the currents are tags maybe already selected, not a screen
  //from which I always start empty (such inserts)
  currentSituationTags?: { externalPropertyId: number; option: string }[];

  showRemovable?: boolean;

  //If true, the mandatory tags will be shown with a star
  showMandatoryAsStar?: boolean;

  whiteBackground?: boolean;
  customStyle?: CSSProperties;

  disabledIds?: number[];
  disabledTooltip?: string;

  //If set, shows only the first N tags and displays +n for remaining tags
  collapseAfter?: number;
  onClick?: MouseEventHandler<HTMLDivElement>;
  className?: string;
}

export const TagsBoxShowAllView = ({
  tags,
  currentSituation,
  currentSituationTags,
  handleClickTag,
  blockIsEditable,
  disableEdit = false,
  showRemovable = true,
  showMandatoryAsStar = false,
  whiteBackground = false,
  customStyle,
  disabledIds,
  disabledTooltip,
  collapseAfter,
  onClick,
  className,
}: TagsBoxShowAllViewProps) => {
  const groupedTags = useMemo(() => {
    return tags.reduce(
      (acc, tag) => {
        if (tag.tableType === 'Account') {
          acc.accounts.push(tag);
        } else if (tag.tableType === 'Prospect') {
          acc.prospects.push(tag);
        } else if (tag.tableType === 'Lead') {
          acc.leads.push(tag);
        } else if (tag.tableType === 'Opportunity') {
          acc.deals.push(tag);
        }
        return acc;
      },
      {
        accounts: [] as ExternalProperty[],
        prospects: [] as ExternalProperty[],
        leads: [] as ExternalProperty[],
        deals: [] as ExternalProperty[],
      },
    );
  }, [tags]);

  // Single open tag — only one dropdown open at a time
  const [openTagId, setOpenTagId] = useState<number | null>(null);

  // Hover state for the +N more popover — must be declared before early return
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [debouncedPopoverOpen, setDebouncedPopoverOpen] = useState(false);
  const debouncedSetOpen = useMemo(
    () => debounce((value: boolean) => setDebouncedPopoverOpen(value), 100),
    [],
  );
  useEffect(() => {
    debouncedSetOpen(popoverOpen);
    return () => debouncedSetOpen.cancel();
  }, [popoverOpen, debouncedSetOpen]);

  const renderTags = (tagsArray: ExternalProperty[]) => {
    return tagsArray.map((externalProperty, indexOfTag) => {
      const isEditable =
        (externalProperty.isEditable || !blockIsEditable) && !disableEdit;

      const option = currentSituation
        ? currentSituation[indexOfTag]
        : (currentSituationTags?.find(
            (tag) => tag.externalPropertyId === externalProperty.id,
          )?.option ?? null);

      // since the order is alphabetical: Account -> Prospect -> Lead
      let indexGeneralArray = 0;
      switch (externalProperty.tableType) {
        case 'Account':
          indexGeneralArray = indexOfTag;
          break;
        case 'Prospect':
          indexGeneralArray = indexOfTag + groupedTags.accounts.length;
          break;
        case 'Lead':
          indexGeneralArray =
            indexOfTag +
            groupedTags.accounts.length +
            groupedTags.prospects.length;
          break;
        case 'Opportunity':
          indexGeneralArray =
            indexOfTag +
            groupedTags.accounts.length +
            groupedTags.prospects.length +
            groupedTags.leads.length;
          break;
      }

      if (option === null && !isEditable) return null;
      const isDisabled =
        disabledIds && disabledIds.includes(externalProperty.id);
      return (
        <Tooltip
          key={externalProperty.id}
          enterDelay={400}
          enterNextDelay={400}
          arrow
          title={isDisabled && disabledTooltip ? disabledTooltip : null}
        >
          <div
            style={{
              opacity: isDisabled ? 0.5 : 1,
            }}
          >
            <TagsBoxSingleTagShowAll
              key={externalProperty.id}
              indexOfTag={indexOfTag}
              indexGeneralArray={indexGeneralArray}
              isEditable={isEditable && !isDisabled}
              option={option}
              tag={externalProperty}
              handleClickTag={handleClickTag}
              showRemovable={showRemovable}
              showMandatoryStar={showMandatoryAsStar}
              whiteBackground={whiteBackground && option === null}
              customStyle={customStyle}
              isOpen={openTagId === externalProperty.id}
              onOpen={() => setOpenTagId(externalProperty.id)}
              onClose={() => setOpenTagId(null)}
            />
          </div>
        </Tooltip>
      );
    });
  };

  if (!collapseAfter) {
    return (
      <div className={cn('flex flex-wrap gap-2', className)} onClick={onClick}>
        {renderTags(groupedTags.accounts)}
        {renderTags(groupedTags.prospects)}
        {renderTags(groupedTags.leads)}
        {renderTags(groupedTags.deals)}
      </div>
    );
  }

  const allRenderableTags: ExternalProperty[] = [];

  let globalIndex = 0;
  [
    groupedTags.accounts,
    groupedTags.prospects,
    groupedTags.leads,
    groupedTags.deals,
  ].forEach((tagsArray) => {
    tagsArray.forEach((externalProperty) => {
      const isEditable =
        (externalProperty.isEditable || !blockIsEditable) && !disableEdit;
      const option = currentSituation
        ? currentSituation[globalIndex]
        : (currentSituationTags?.find(
            (tag) => tag.externalPropertyId === externalProperty.id,
          )?.option ?? null);

      if (option !== null || isEditable) {
        allRenderableTags.push(externalProperty);
      }
      globalIndex++;
    });
  });

  const visibleTags = allRenderableTags.slice(0, collapseAfter);
  const remainingTags = allRenderableTags.slice(collapseAfter);
  const remainingCount = remainingTags.length;

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      onClick={onClick}
    >
      {renderTags(visibleTags)}
      {remainingCount > 0 && (
        <Popover open={debouncedPopoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger
            asChild
            onMouseEnter={() => setPopoverOpen(true)}
            onMouseLeave={() => setPopoverOpen(false)}
            onClick={(e) => e.preventDefault()}
          >
            <div
              role="button"
              tabIndex={0}
              className="h-7 min-w-7 rounded-lg p-1 flex items-center justify-center bg-gray-200 text-xs font-medium cursor-default"
            >
              +{remainingCount}
            </div>
          </PopoverTrigger>
          <PopoverContent
            aria-label="Remaining tags"
            onMouseEnter={() => setPopoverOpen(true)}
            onMouseLeave={() => setPopoverOpen(false)}
            className="p-2 flex flex-col gap-1.5 w-fit max-w-60"
          >
            {remainingTags.map((externalProperty) => {
              let indexOfTag = 0;
              let indexGeneralArray = 0;
              switch (externalProperty.tableType) {
                case 'Account':
                  indexOfTag = groupedTags.accounts.indexOf(externalProperty);
                  indexGeneralArray = indexOfTag;
                  break;
                case 'Prospect':
                  indexOfTag = groupedTags.prospects.indexOf(externalProperty);
                  indexGeneralArray = indexOfTag + groupedTags.accounts.length;
                  break;
                case 'Lead':
                  indexOfTag = groupedTags.leads.indexOf(externalProperty);
                  indexGeneralArray =
                    indexOfTag +
                    groupedTags.accounts.length +
                    groupedTags.prospects.length;
                  break;
                case 'Opportunity':
                  indexOfTag = groupedTags.deals.indexOf(externalProperty);
                  indexGeneralArray =
                    indexOfTag +
                    groupedTags.accounts.length +
                    groupedTags.prospects.length +
                    groupedTags.leads.length;
                  break;
              }

              const option =
                currentSituationTags?.find(
                  (tag) => tag.externalPropertyId === externalProperty.id,
                )?.option ?? null;

              return (
                <TagsBoxSingleTagShowAll
                  key={externalProperty.id}
                  indexOfTag={indexOfTag}
                  indexGeneralArray={indexGeneralArray}
                  isEditable={false}
                  option={option}
                  tag={externalProperty}
                  showRemovable={false}
                  showMandatoryStar={showMandatoryAsStar}
                  customStyle={{ maxWidth: '100%' }}
                />
              );
            })}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
